import { IXmlNode } from '@/app-modules/server-communications';
import { IPrizeStructure, IPrizeStake } from '@/components/game/implementations/_default/_domain/models';
import { CatchUpData, ResetData, CallsData, NextGameData, PrizeData } from '@/components/game/_domain/server/prize/prize-data';
import { safeCast } from '@/helper';
import { BingoLitePrizeType, getPrizeTypeName } from '@/components/game/implementations/uk/bingo-lite/_domain/models';
import { getGameConfiguration } from '@/components/game/_domain/helpers';
import { ConfigurationPaths } from '@/app-modules/config/models';

class BingoLitePrizeData extends PrizeData {
  constructor(node: IXmlNode) {
    super(node);
  }

  private get prizeTypes(): BingoLitePrizeType[] {
    return [
      BingoLitePrizeType.Jackpot,
      BingoLitePrizeType.FullHouse,
      BingoLitePrizeType.TwoLine,
      BingoLitePrizeType.OneLine
    ];
  }

  private get jackpotCall(): number {
    const gameConfiguration = getGameConfiguration();
    const gameConfigurationPaths = ConfigurationPaths.forGame('bingolite');
    return Number(gameConfiguration.getConfigurationByPath(gameConfigurationPaths.jackpotcalls));
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    return {
      stakes: [this.getPrizeStake(node)]
    };
  }

  private getPrizeStake(node: IXmlNode): IPrizeStake {
    return {
      stakeValue: 0,
      ticketBooks: [
        {
          id: 'default',
          name: 'Default',
          prizes: this.getValidPrizeTypes(node).map((prizeType) => {
            const isJackpot = prizeType === BingoLitePrizeType.Jackpot;

            return {
              name: getPrizeTypeName(prizeType),
              prize: this.getPrizeValueForPrizeType(prizeType, node),
              disablePrize: false,
              isJackpot,
              jackpotCalls: isJackpot ? this.jackpotCall : 0
            };
          })
        }
      ]
    };
  }

  private getValidPrizeTypes(node: IXmlNode): BingoLitePrizeType[] {
    const isLinkedGame = safeCast(node.attributes.linked, false);

    // Two line prize is only available on linked games
    return this.prizeTypes.filter((prizeType) => {
      return isLinkedGame || prizeType !== BingoLitePrizeType.TwoLine;
    });
  }

  private getPrizeValueForPrizeType(prizeType: BingoLitePrizeType, node: IXmlNode): number {
    const prizeAttributeName = `${prizeType}`;

    if (node.attributes[prizeAttributeName]) {
      return safeCast(node.attributes[prizeAttributeName], 0);
    }
    return prizeType === BingoLitePrizeType.FullHouse ? safeCast(node.attributes.prize, 0) : 0;
  }
}

class BingoLiteCatchUpData extends CatchUpData {
  private prizeData: BingoLitePrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new BingoLitePrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class BingoLiteResetData extends ResetData {
  private prizeData: BingoLitePrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new BingoLitePrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class BingoLiteCallsData extends CallsData {
  constructor(node: IXmlNode) {
    super(node);
  }
}

class BingoLiteNextGameData extends NextGameData {
  private prizeData: BingoLitePrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new BingoLitePrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

export {
  BingoLiteCatchUpData,
  BingoLiteResetData,
  BingoLiteCallsData,
  BingoLiteNextGameData
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/bingo-lite/_domain/models/prize.ts